import PropTypes from 'prop-types';
import { useLayoutEffect, useRef, useState } from 'react';
import Icon from '@/components/common/iconedLinks/Icon';
import { URLS } from '@/constants/urls';
import styles from './Accordion.module.scss';

const Accordion = ({
	id,
	label,
	children,
	onClick,
	open,
	className,
	labelClassName,
	showArrow = true,
	increasedPadding = false,
	iconLazy = false,
}) => {
	const childRef = useRef();
	const [accordionHeight, setAccordionHeight] = useState('0');

	const handleClick = () => {
		onClick?.();
	};

	useLayoutEffect(() => {
		const updateHeight = () => {
			if (open) {
				setAccordionHeight(`${childRef.current?.scrollHeight}px`);
			}
		};

		const observer = new ResizeObserver(updateHeight);
		observer.observe(childRef.current);

		updateHeight();

		return () => observer.disconnect();
	}, [children]);

	useLayoutEffect(() => {
		setAccordionHeight(open ? childRef.current?.clientHeight : '0');
	}, [open]);

	const containerClassName = [styles.container, className].filter(Boolean).join(' ');

	const buttonClassName = [styles.button, increasedPadding && styles['increased-padding']].filter(Boolean).join(' ');

	const computeLabelClassName = [labelClassName || styles.label].filter(Boolean);

	return (
		<div id={id} className={containerClassName} data-rbf={`accordion${id ? '-' + id : ''}`} data-test-id="accordion">
			<div className={buttonClassName} data-test-id="button" data-rbf="button" onClick={handleClick}>
				<div className={computeLabelClassName} data-test-id="label" data-rbf="label">
					{label}
				</div>
				{showArrow ? (
					<Icon
						src={open ? URLS.ANGLE_BOTTOM_BLACK : URLS.ANGLE_RIGHT_BLACK}
						alt={open ? 'down' : 'right'}
						lazy={iconLazy}
						width={24}
						height={24}
					/>
				) : (
					<Icon
						src={open ? URLS.ICON_MINUS : URLS.BLACK_ICON_ADD}
						alt={open ? 'minus' : 'plus'}
						className={styles.icon}
						lazy={iconLazy}
					/>
				)}
			</div>
			<div
				className={`${styles.content} ${open ? styles.show : styles.hide}`}
				style={{ maxHeight: accordionHeight }}
				role="button"
				aria-expanded={open}
				data-test-id="content"
				data-rbf="content"
			>
				<div className={styles.children} ref={childRef}>
					{children}
				</div>
			</div>
		</div>
	);
};

Accordion.propTypes = {
	id: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	children: PropTypes.node.isRequired,
	onClick: PropTypes.func,
	open: PropTypes.bool,
	className: PropTypes.string,
	labelClassName: PropTypes.string,
	showArrow: PropTypes.bool,
	increasedPadding: PropTypes.bool,
	iconLazy: PropTypes.bool,
};

export default Accordion;
